import { useRef, useState } from "react";
import { Upload, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { readFileAsDataURL, downsizeImage, classifyUpload } from "@/lib/utils";
import { uploadImage, analyzeImage } from "@/api";
import DocumentImportDialog from "@/components/DocumentImportDialog";

const isPdf = (f) =>
  f?.type === "application/pdf" || (f?.name || "").toLowerCase().endsWith(".pdf");

export default function ImageUploader({
  projectId,
  category = "inspiration",
  onUploaded,
  autoAnalyze = true,
  label = "Add",
  testid = "upload-images-button",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [pdfQueue, setPdfQueue] = useState([]);
  const [docOpen, setDocOpen] = useState(false);

  async function handleFiles(files) {
    if (!files?.length) return;
    const pdfs = Array.from(files).filter(isPdf);
    const others = Array.from(files).filter((f) => !isPdf(f));
    if (others.length) await uploadDirect(others);
    if (pdfs.length) {
      setPdfQueue(pdfs);
      setDocOpen(true);
    }
  }

  async function uploadDirect(files) {
    if (!files?.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const { kind, category: targetCategory, mime } = classifyUpload(file, category);
      try {
        const raw = await readFileAsDataURL(file);
        const data = kind === "image" ? await downsizeImage(raw, 1600, 0.85) : raw;
        const created = await uploadImage(projectId, {
          project_id: projectId,
          data,
          filename: file.name,
          category: targetCategory,
          mime,
        });
        uploaded.push(created);
        setProgress({ done: i + 1, total: files.length });
        if (autoAnalyze && kind === "image") {
          analyzeImage({ image_data: data, image_id: created.id })
            .then((res) => onUploaded?.({ ...created, ...res, ai_analyzed: true }))
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        toast.error(`Failed: ${file.name}`);
      }
    }
    setBusy(false);
    setProgress({ done: 0, total: 0 });
    toast.success(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} added`);
    uploaded.forEach((u) => onUploaded?.(u));
  }

  return (
    <>
      <DocumentImportDialog
        open={docOpen}
        onOpenChange={setDocOpen}
        files={pdfQueue}
        projectId={projectId}
        category={category}
        onProcessed={(item) => onUploaded?.(item)}
      />
      <input
        ref={inputRef}
        type="file"
        accept="*/*"
        multiple
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          handleFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={testid}
        className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Uploading {progress.done}/{progress.total}
          </>
        ) : (
          <>
            {autoAnalyze ? (
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
            ) : (
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            {label}
          </>
        )}
      </Button>
    </>
  );
}
